import type { StateCreator } from 'zustand';
import type {
  LinkedInPost,
  TextViolation,
  QuellenErklaerung,
  LinkedInBrief,
  LinkedInKandidat,
  LinkedInJudgeUrteil,
  LinkedInPostFormat,
  LinkedInTensionCategory,
  LinkedInHookFormula,
  LinkedInExperiment,
  LinkedInToneSliders,
  LinkedInLength,
  LinkedInPhase,
  LinkedInGenerateRequest,
  LinkedInHookOption,
  LinkedInHookSkeleton,
  LinkedInAngleOption,
  LinkedInLaunchSuggestRequest,
  LinkedInLaunchSuggestResponse,
  LinkedInLaunchGenerateRequest,
  LinkedInLaunchGenerateResponse,
  LinkedInCockpitPreset,
  LinkedInPromptFragment,
} from '../types/linkedin';
import {
  fetchLinkedInPosts as apiFetchLinkedInPosts,
  generateLinkedInPost as apiGenerateLinkedInPost,
  waehleLinkedInKandidat as apiWaehleLinkedInKandidat,
  lehneLinkedInKandidatenAb as apiLehneLinkedInKandidatenAb,
  previewLinkedInPrompt as apiPreviewLinkedInPrompt,
  suggestLinkedInHooks as apiSuggestLinkedInHooks,
  browseHookLibrary as apiBrowseHookLibrary,
  fillHookFromLibrary as apiFillHookFromLibrary,
  listHookVariants as apiListHookVariants,
  editHookVariant as apiEditHookVariant,
  deleteHookVariant as apiDeleteHookVariant,
  suggestLinkedInAngles as apiSuggestLinkedInAngles,
  branchLinkedInPost as apiBranchLinkedInPost,
  rewriteLinkedInPost as apiRewriteLinkedInPost,
  suggestLaunchSequence as apiSuggestLaunchSequence,
  generateLaunchSequence as apiGenerateLaunchSequence,
  fetchLinkedInPresets as apiFetchLinkedInPresets,
  createLinkedInPreset as apiCreateLinkedInPreset,
  updateLinkedInPreset as apiUpdateLinkedInPreset,
  deleteLinkedInPreset as apiDeleteLinkedInPreset,
  fetchLinkedInPromptFragments as apiFetchLinkedInPromptFragments,
  saveLinkedInPromptFragment as apiSaveLinkedInPromptFragment,
  resetLinkedInPromptFragment as apiResetLinkedInPromptFragment,
} from '../services/linkedinService';
import { contentStudioApi } from '../services/contentStudioApi';
import type { EngineConfig, CockpitScores } from '../services/contentStudioApi';

// Anzeige-Daten der Vault-Quelle fuer den Vorlagen-Layer. Getrennt vom
// Request-Feld `vault_source` (types/linkedin.ts LinkedInVaultSource), das nur
// post_id + mode traegt — dies hier ist reine Darstellung im Frontend.
export interface VaultSourceMeta {
  postId: number;
  author: string;
  platform: string;
  viralScore: number | null;
  url: string | null;
  excerpt: string;
}

export interface LinkedInCockpitForm {
  strategyId: number | null;
  language: 'en' | 'de';
  postFormat: LinkedInPostFormat | null;
  tensionCategory: LinkedInTensionCategory | null; // null = auto
  hookFormula: LinkedInHookFormula | null; // null = auto
  topicAngle: string;
  customRules: string;
  toneSliders: LinkedInToneSliders;
  length: LinkedInLength;
  phase: LinkedInPhase | null; // null = auto-derive
  experiments: LinkedInExperiment[];
  documentIds: number[];
  useLeadInsights: boolean;
  // Der Brief (Stufe B): zusaetzliche Quellen-Schalter neben Dokumenten/Kennzahlen, die schon
  // eigene Regler haben (documentIds/useLeadInsights). Default an -- ausschalten ist die
  // Ausnahme, damit sichtbar wird, was pro Quelle wirkt.
  briefLeadAggregat: boolean;
  briefOutreachAntworten: boolean;
  briefTelefonNotizen: boolean;
  stylePresetId: number | ''; // '' = no preset
  useCompetitor: boolean;
  competitorPostIds: number[];
  // Vorlage aus dem Content Vault. Bewusst NICHT in LINKEDIN_PRESET_FIELDS — ein
  // gespeichertes Preset darf nicht still die Vorlage eines fremden Posts eines
  // spaeteren Aufrufs ueberschreiben.
  vaultSourcePostId: number | null;
  vaultSourceMode: 'skeleton' | 'rewrite';
  vaultSourceMeta: VaultSourceMeta | null;
  model: 'haiku' | 'sonnet';
  promptOverride: string | null; // edited user message from the Inspector
}

// Felder, die ein Preset speichert. Bewusst eine explizite Liste statt
// "alles ausser X": strategyId kommt aus dem Kontext, promptOverride ist ein
// Einzelfall-Eingriff, der nicht still in jedem geladenen Preset weiterwirken darf.
// Neue Formularfelder muessen hier eingetragen werden, sonst speichert das
// Preset sie nicht — das ist Absicht (bewusste Entscheidung pro Feld).
export const LINKEDIN_PRESET_FIELDS = [
  'language',
  'postFormat',
  'length',
  'phase',
  'tensionCategory',
  'hookFormula',
  'topicAngle',
  'customRules',
  'toneSliders',
  'experiments',
  'documentIds',
  'useLeadInsights',
  'briefLeadAggregat',
  'briefOutreachAntworten',
  'briefTelefonNotizen',
  'stylePresetId',
  'useCompetitor',
  'competitorPostIds',
  'model',
] as const satisfies readonly (keyof LinkedInCockpitForm)[];

export type LinkedInPresetConfig = Partial<Pick<LinkedInCockpitForm, (typeof LINKEDIN_PRESET_FIELDS)[number]>>;

export function extractPresetConfig(form: LinkedInCockpitForm): LinkedInPresetConfig {
  const out: Record<string, unknown> = {};
  for (const k of LINKEDIN_PRESET_FIELDS) out[k] = form[k];
  return out as LinkedInPresetConfig;
}

/** Ein gespeichertes Preset auf ein gueltiges Form-Patch abbilden.
 *  Unbekannte Schluessel und Werte mit falschem Typ werden verworfen, damit ein
 *  altes Preset nach einer Umbenennung nichts kaputt macht. */
export function presetConfigToPatch(config: Record<string, unknown>): Partial<LinkedInCockpitForm> {
  const patch: Record<string, unknown> = {};
  const isStr = (v: unknown) => typeof v === 'string';
  const numArray = (v: unknown) => (Array.isArray(v) ? v.filter((x) => typeof x === 'number') : null);

  if (config.language === 'en' || config.language === 'de') patch.language = config.language;
  if (config.postFormat === null || isStr(config.postFormat)) patch.postFormat = config.postFormat ?? null;
  if (isStr(config.length)) patch.length = config.length;
  if (config.phase === null || isStr(config.phase)) patch.phase = config.phase ?? null;
  if (config.tensionCategory === null || isStr(config.tensionCategory))
    patch.tensionCategory = config.tensionCategory ?? null;
  if (config.hookFormula === null || isStr(config.hookFormula)) patch.hookFormula = config.hookFormula ?? null;
  if (isStr(config.topicAngle)) patch.topicAngle = (config.topicAngle as string).slice(0, 5000);
  if (isStr(config.customRules)) patch.customRules = (config.customRules as string).slice(0, 2000);
  if (config.toneSliders && typeof config.toneSliders === 'object') {
    patch.toneSliders = { ...LINKEDIN_TONE_DEFAULTS, ...(config.toneSliders as object) };
  }
  if (Array.isArray(config.experiments)) patch.experiments = config.experiments.filter(isStr);
  const docs = numArray(config.documentIds);
  if (docs) patch.documentIds = docs;
  if (typeof config.useLeadInsights === 'boolean') patch.useLeadInsights = config.useLeadInsights;
  if (typeof config.briefLeadAggregat === 'boolean') patch.briefLeadAggregat = config.briefLeadAggregat;
  if (typeof config.briefOutreachAntworten === 'boolean')
    patch.briefOutreachAntworten = config.briefOutreachAntworten;
  if (typeof config.briefTelefonNotizen === 'boolean')
    patch.briefTelefonNotizen = config.briefTelefonNotizen;
  if (config.stylePresetId === '' || typeof config.stylePresetId === 'number')
    patch.stylePresetId = config.stylePresetId;
  if (typeof config.useCompetitor === 'boolean') patch.useCompetitor = config.useCompetitor;
  const comps = numArray(config.competitorPostIds);
  if (comps) patch.competitorPostIds = comps;
  if (config.model === 'haiku' || config.model === 'sonnet') patch.model = config.model;
  return patch as Partial<LinkedInCockpitForm>;
}

export const LINKEDIN_TONE_DEFAULTS: LinkedInToneSliders = {
  aggressiveness: 50,
  formality: 60, // LinkedIn defaults slightly more formal than IG
  aspect_focus: 'balanced',
  value_depth: 50,
};

export function createEmptyLinkedInCockpitForm(): LinkedInCockpitForm {
  return {
    strategyId: null,
    language: 'en',
    postFormat: null,
    tensionCategory: null,
    hookFormula: null,
    topicAngle: '',
    customRules: '',
    toneSliders: { ...LINKEDIN_TONE_DEFAULTS },
    length: 'medium',
    phase: null,
    // Full Power by default: all prompt-lab layers on (original generation,
    // rich strategy context, sharp hook/caption rules, guided generation).
    experiments: ['original_mode', 'rich_context', 'sharp_rules', 'guided_gen'],
    documentIds: [],
    useLeadInsights: false,
    briefLeadAggregat: true,
    briefOutreachAntworten: true,
    briefTelefonNotizen: true,
    stylePresetId: '',
    useCompetitor: false,
    competitorPostIds: [],
    vaultSourcePostId: null,
    vaultSourceMode: 'skeleton',
    vaultSourceMeta: null,
    model: 'sonnet',
    promptOverride: null,
  };
}

export interface LinkedInSlice {
  // Queue tab state (existing)
  linkedinPosts: LinkedInPost[];
  linkedinLoading: boolean;
  linkedinGenerating: boolean;
  linkedinSelectedStrategyId: number | null;
  linkedinActiveTab: 'create' | 'queue';
  // Warteplatz fuer eine Vault-Uebergabe. Nur 422-unsichere Felder liegen hier zwischen:
  // gesetzt wird `hookFormula` erst, wenn die engineConfig da ist und die id bestaetigt.
  linkedinPendingVaultHandover: { hookFormula: string | null } | null;
  // Gesetzt genau EINMAL, wenn `applyLinkedInVaultHandover` durchgelaufen ist —
  // die Create-Tab-Oberflaeche liest es, klappt die betroffenen Karten auf, zeigt
  // einen Hinweis und loescht es danach wieder. Kein Persistenzzustand.
  linkedinVaultHandoverJustApplied: VaultSourceMeta | null;

  // Create tab / cockpit state
  linkedinCockpitForm: LinkedInCockpitForm;
  linkedinEngineConfig: EngineConfig | null;
  linkedinEngineConfigLoading: boolean;
  linkedinCockpitScores: CockpitScores | null;
  linkedinCockpitScoresLoading: boolean;
  linkedinGeneratedContent: string | null;
  // content_generations.id der zuletzt erzeugten (echten) Generierung -- Schluessel fuer das
  // Glass-Box-Manifest (GET /linkedin/generations/{id}/layers). null im Test-Modus.
  linkedinGeneratedId: number | null;
  linkedinGeneratedPromptSystem: string | null;
  linkedinGeneratedPromptUser: string | null;
  linkedinGeneratedIsTestMode: boolean;
  // Quellen-Fluss: Erklaerung + Funde aus der Generate-Antwort (Saetze kommen nachgeladen)
  linkedinGeneratedQuellen: { erklaerung: QuellenErklaerung; funde: TextViolation[]; badge: boolean } | null;
  // Der Brief (Stufe B): was die Layer fuer DIESEN Post gefunden haben -- fuer die Karten.
  linkedinGeneratedBrief: LinkedInBrief | null;
  // Stufe D: bis zu drei blinde Kandidaten aus /generate -- der Nutzer waehlt, dann erst
  // erscheint das Judge-Urteil. Waehrend `linkedinKandidaten` gesetzt ist, zeigt die
  // Oberflaeche die Wahl-Karten statt eines fertigen Posts.
  linkedinKandidaten: LinkedInKandidat[] | null;
  linkedinKandidatenPostId: number | null;
  linkedinVerworfenAnzahl: number;
  linkedinWahlLoading: boolean;
  linkedinAblehnenLoading: boolean;
  // Nach der Wahl aufgedeckt (nie vorher) -- Empfehlung + Punkte je Kandidat.
  linkedinJudgeUrteil: LinkedInJudgeUrteil | null;
  linkedinJudgeUebereinstimmung: boolean | null;
  linkedinGenerateError: string | null;
  linkedinPromptPreview: { system: string; user: string } | null;
  linkedinPromptPreviewLoading: boolean;

  // Hook picker state (mirror reel HookPicker): cache-first, no tokens on reopen
  linkedinHookPickerOpen: boolean;
  linkedinHookOptions: LinkedInHookOption[];
  linkedinHookLoading: boolean;
  linkedinHookFromCache: boolean;
  linkedinHookError: string | null;

  // Hook library state — the 995 raw skeletons. Browsing is FREE; only filling
  // one chosen skeleton costs a call. `linkedinHooksLoaded` gates the AI fetch so
  // opening the dialog no longer spends anything.
  linkedinHooksLoaded: boolean;
  linkedinLibItems: LinkedInHookSkeleton[];
  linkedinLibCounts: Record<string, number>;
  linkedinLibTotal: number;
  linkedinLibCategory: string | null;
  linkedinLibQuery: string;
  linkedinLibLoading: boolean;
  linkedinLibLoadingMore: boolean;
  linkedinLibError: string | null;
  linkedinLibFillingId: string | null;
  linkedinLibDraft: LinkedInHookOption | null;
  linkedinLibDraftNeedsNumber: boolean;
  linkedinLibVariants: LinkedInHookOption[];

  // Angle picker state (document-grounded angles; cache-first per document set)
  linkedinAnglePickerOpen: boolean;
  linkedinAngleOptions: LinkedInAngleOption[];
  linkedinAngleLoading: boolean;
  linkedinAngleFromCache: boolean;
  linkedinAngleError: string | null;

  // Branch (hook A/B) state: pick a new hook for an existing post's body
  linkedinBranchPickerOpen: boolean;
  linkedinBranchTargetId: number | null;
  linkedinBranchTargetStrategyId: number | null;
  linkedinBranchTopicHint: string;
  linkedinBranchHookOptions: LinkedInHookOption[];
  linkedinBranchLoading: boolean;
  linkedinBranchFromCache: boolean;
  linkedinBranchError: string | null;
  linkedinBranching: boolean;

  // Grounding rewrite ("Neu formulieren"): id of the post currently being rewritten
  linkedinRewritingId: number | null;

  // Launch sequence (page cold-start) state
  linkedinLaunchPlan: LinkedInLaunchSuggestResponse | null;
  linkedinLaunchSuggesting: boolean;
  linkedinLaunchGenerating: boolean;
  linkedinLaunchResults: LinkedInLaunchGenerateResponse | null;
  linkedinLaunchError: string | null;

  // Queue tab actions (existing)
  setLinkedInPosts: (posts: LinkedInPost[]) => void;
  setLinkedInGenerating: (v: boolean) => void;
  setLinkedInSelectedStrategyId: (id: number | null) => void;
  setLinkedInActiveTab: (tab: 'create' | 'queue') => void;
  fetchLinkedInPosts: (strategyId?: number) => Promise<void>;
  updateLinkedInPost: (id: number, patch: Partial<LinkedInPost>) => void;

  // Cockpit actions
  setLinkedInCockpitForm: (patch: Partial<LinkedInCockpitForm>) => void;
  setLinkedInCockpitStrategy: (strategyId: number | null) => void;
  fetchLinkedInEngineConfig: (strategyId: number) => Promise<void>;
  fetchLinkedInCockpitScores: (strategyId: number) => Promise<void>;
  previewLinkedInPrompt: () => Promise<void>;
  clearLinkedInPromptPreview: () => void;
  generateLinkedInPostWithCockpit: (opts?: { testMode?: boolean; selectedHook?: LinkedInHookOption }) => Promise<void>;
  clearLinkedInGenerated: () => void;
  // Stufe D: blind einen der generierten Kandidaten waehlen -- deckt danach das Judge-Urteil auf.
  waehleLinkedInKandidat: (kandidatIndex: number) => Promise<void>;
  // "keiner, neu": archiviert die aktuelle Wahl-Zeile mit Gruenden und generiert sofort neu.
  lehneLinkedInKandidatenAb: (gruende: string[]) => Promise<void>;

  // Hook picker actions
  openLinkedInHookPicker: () => Promise<void>; // opens on the FREE library, no AI call
  ensureLinkedInHooks: () => Promise<void>; // cache-first suggest, on entering the AI tab
  rerollLinkedInHooks: () => Promise<void>; // force fresh hooks (spends tokens)
  pickLinkedInHook: (opt: LinkedInHookOption) => Promise<void>; // anchor + generate
  closeLinkedInHookPicker: () => void;

  // Hook library actions (free unless noted)
  loadLinkedInLibrary: (opts?: { append?: boolean }) => Promise<void>;
  setLinkedInLibraryCategory: (c: string | null) => Promise<void>;
  setLinkedInLibraryQuery: (q: string) => void;
  fillLinkedInSkeleton: (hook: LinkedInHookSkeleton) => Promise<void>; // ONE paid call
  saveLinkedInVariantEdit: (cacheId: number, text: string) => Promise<void>;
  deleteLinkedInVariant: (cacheId: number) => Promise<void>;
  useLinkedInVariant: (opt: LinkedInHookOption) => void;
  discardLinkedInDraft: () => void;

  // Angle picker actions
  openLinkedInAnglePicker: () => Promise<void>; // cache-first suggest + open modal
  rerollLinkedInAngles: () => Promise<void>; // force fresh angles (spends tokens)
  pickLinkedInAngle: (opt: LinkedInAngleOption) => void; // fill topicAngle + close
  // Vault -> Content Engine. Stufe A (synchron, 422-sicher) bzw. Stufe B (nach engineConfig).
  startLinkedInVaultHandover: (payload: {
    topicAngle: string;
    hookFormula: string | null;
    strategyId: number | null;
    vaultSourceMeta: VaultSourceMeta | null;
  }) => void;
  applyLinkedInVaultHandover: () => void;
  clearLinkedInVaultHandoverBanner: () => void;
  closeLinkedInAnglePicker: () => void;

  // Branch (hook A/B) actions
  openLinkedInBranchPicker: (post: LinkedInPost) => Promise<void>; // hooks for this post's body
  rerollLinkedInBranchHooks: () => Promise<void>;
  pickLinkedInBranchHook: (opt: LinkedInHookOption) => Promise<void>; // create the variant
  closeLinkedInBranchPicker: () => void;

  // Grounding rewrite ("Neu formulieren"): non-destructive, returns proposed text
  // for the editor (does NOT persist). null on failure.
  rewriteLinkedInPost: (id: number, targetExcerpt?: string) => Promise<string | null>;

  // Launch sequence actions
  suggestLaunch: (req: LinkedInLaunchSuggestRequest) => Promise<void>;
  generateLaunch: (req: LinkedInLaunchGenerateRequest) => Promise<void>;
  clearLaunch: () => void;

  // Cockpit-Presets (benannte Reglerstaende pro Strategie)
  linkedinPresets: LinkedInCockpitPreset[];
  linkedinPresetsLoading: boolean;
  linkedinActivePresetId: number | null;
  linkedinPresetDirty: boolean;
  linkedinPresetError: string | null;
  fetchLinkedInPresets: (strategyId: number) => Promise<void>;
  saveLinkedInPreset: (name: string) => Promise<void>;
  updateActiveLinkedInPreset: () => Promise<void>;
  applyLinkedInPreset: (presetId: number) => void;
  removeLinkedInPreset: (presetId: number) => Promise<void>;
  clearActiveLinkedInPreset: () => void;

  // Prompt-Bausteine (was hinter jedem Regler wirklich im Prompt steht)
  linkedinPromptFragments: LinkedInPromptFragment[];
  linkedinPromptFragmentsLoading: boolean;
  linkedinPromptFragmentsLoaded: boolean;
  linkedinPromptFragmentsCanEdit: boolean;
  fetchLinkedInPromptFragments: (opts?: { force?: boolean }) => Promise<void>;
  saveLinkedInPromptFragment: (key: string, text: string) => Promise<void>;
  resetLinkedInPromptFragment: (key: string) => Promise<void>;
}

function buildRequestBody(
  form: LinkedInCockpitForm,
  testMode: boolean,
  selectedHook?: LinkedInHookOption,
): LinkedInGenerateRequest {
  if (!form.strategyId) throw new Error('strategy_id required');
  const body: LinkedInGenerateRequest = {
    strategy_id: form.strategyId,
    language: form.language,
    topic_angle: form.topicAngle.trim() || undefined,
    custom_rules: form.customRules.trim() || undefined,
    tension_category: form.tensionCategory,
    hook_formula: form.hookFormula,
    post_format: form.postFormat,
    experiments: form.experiments,
    tone_sliders: form.toneSliders,
    length: form.length,
    phase: form.phase,
    test_mode: testMode,
    document_ids: form.documentIds.length > 0 ? form.documentIds : undefined,
    use_lead_insights: form.useLeadInsights || undefined,
    style_preset_id: form.stylePresetId === '' ? null : form.stylePresetId,
    competitor_post_ids:
      form.useCompetitor && form.competitorPostIds.length > 0 ? form.competitorPostIds : undefined,
    vault_source: form.vaultSourcePostId
      ? { post_id: form.vaultSourcePostId, mode: form.vaultSourceMode }
      : undefined,
    // Der Brief holt sich Dokumente/Kennzahlen/Vault/Referenzen ueber die bestehenden Regler
    // (documentIds/useLeadInsights/vaultSourcePostId); hier nur die drei zusaetzlichen
    // Quellen-Schalter, die noch keinen eigenen Regler haben.
    brief_quellen: {
      lead_aggregat: form.briefLeadAggregat,
      outreach_antworten: form.briefOutreachAntworten,
      telefon_notizen: form.briefTelefonNotizen,
    },
    model: form.model,
    // Override only applies to real generation, never test mode / preview
    prompt_override: !testMode && form.promptOverride ? form.promptOverride : undefined,
  };
  // Anchor the post on a picked hook (real generation only).
  if (!testMode && selectedHook) {
    body.selected_hook = {
      hook_id: selectedHook.hook_id ?? null,
      filled_text: selectedHook.filled_text,
      template: selectedHook.template ?? null,
      category: selectedHook.category ?? null,
    };
    body.grounding = selectedHook.grounding ?? null;
    if (selectedHook.cache_id) body.hook_cache_id = selectedHook.cache_id;
  }
  return body;
}

export const createLinkedInSlice: StateCreator<LinkedInSlice> = (set, get) => ({
  // Queue state
  linkedinPosts: [],
  linkedinLoading: false,
  linkedinGenerating: false,
  linkedinSelectedStrategyId: null,
  linkedinActiveTab: 'create',
  linkedinPendingVaultHandover: null,
  linkedinVaultHandoverJustApplied: null,

  // Cockpit state
  linkedinCockpitForm: createEmptyLinkedInCockpitForm(),
  linkedinEngineConfig: null,
  linkedinEngineConfigLoading: false,
  linkedinCockpitScores: null,
  linkedinCockpitScoresLoading: false,
  linkedinGeneratedContent: null,
  linkedinGeneratedId: null,
  linkedinGeneratedPromptSystem: null,
  linkedinGeneratedPromptUser: null,
  linkedinGeneratedIsTestMode: false,
  linkedinGeneratedQuellen: null,
  linkedinGeneratedBrief: null,
  linkedinKandidaten: null,
  linkedinKandidatenPostId: null,
  linkedinVerworfenAnzahl: 0,
  linkedinWahlLoading: false,
  linkedinAblehnenLoading: false,
  linkedinJudgeUrteil: null,
  linkedinJudgeUebereinstimmung: null,
  linkedinGenerateError: null,
  linkedinPromptPreview: null,
  linkedinPromptPreviewLoading: false,

  // Hook picker state
  linkedinHookPickerOpen: false,
  linkedinHookOptions: [],
  linkedinHookLoading: false,
  linkedinHookFromCache: false,
  linkedinHookError: null,

  // Hook library state
  linkedinHooksLoaded: false,
  linkedinLibItems: [],
  linkedinLibCounts: {},
  linkedinLibTotal: 0,
  linkedinLibCategory: null,
  linkedinLibQuery: '',
  linkedinLibLoading: false,
  linkedinLibLoadingMore: false,
  linkedinLibError: null,
  linkedinLibFillingId: null,
  linkedinLibDraft: null,
  linkedinLibDraftNeedsNumber: false,
  linkedinLibVariants: [],

  // Angle picker state
  linkedinAnglePickerOpen: false,
  linkedinAngleOptions: [],
  linkedinAngleLoading: false,
  linkedinAngleFromCache: false,
  linkedinAngleError: null,

  // Branch (hook A/B) state
  linkedinBranchPickerOpen: false,
  linkedinBranchTargetId: null,
  linkedinBranchTargetStrategyId: null,
  linkedinBranchTopicHint: '',
  linkedinBranchHookOptions: [],
  linkedinBranchLoading: false,
  linkedinBranchFromCache: false,
  linkedinBranchError: null,
  linkedinBranching: false,
  linkedinRewritingId: null,

  // Launch state
  linkedinLaunchPlan: null,
  linkedinLaunchSuggesting: false,
  linkedinLaunchGenerating: false,
  linkedinLaunchResults: null,
  linkedinLaunchError: null,

  // Queue actions
  setLinkedInPosts: (posts) => set({ linkedinPosts: posts }),
  setLinkedInGenerating: (v) => set({ linkedinGenerating: v }),
  setLinkedInSelectedStrategyId: (id) => set({ linkedinSelectedStrategyId: id }),
  setLinkedInActiveTab: (tab) => set({ linkedinActiveTab: tab }),

  fetchLinkedInPosts: async (strategyId) => {
    set({ linkedinLoading: true });
    try {
      const result = await apiFetchLinkedInPosts(
        strategyId !== undefined ? { strategy_id: strategyId } : undefined,
      );
      set({ linkedinPosts: result.posts, linkedinLoading: false });
    } catch {
      set({ linkedinLoading: false });
    }
  },

  updateLinkedInPost: (id, patch) => {
    set((s) => ({
      linkedinPosts: s.linkedinPosts.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    }));
  },

  // Cockpit actions
  setLinkedInCockpitForm: (patch) => {
    set((s) => ({
      linkedinCockpitForm: { ...s.linkedinCockpitForm, ...patch },
      // Form change invalidates prompt preview (stale)
      linkedinPromptPreview: null,
      // Ein geladenes Preset weicht ab, sobald ein Regler bewegt wird — nur bei
      // Feldern, die das Preset ueberhaupt speichert (promptOverride zaehlt nicht).
      linkedinPresetDirty:
        s.linkedinActivePresetId !== null
          ? s.linkedinPresetDirty ||
            Object.keys(patch).some((k) => (LINKEDIN_PRESET_FIELDS as readonly string[]).includes(k))
          : false,
    }));
  },

  setLinkedInCockpitStrategy: (strategyId) => {
    // Full reset of cockpit-derived state when strategy changes.
    // documentIds are strategy-scoped, so we reset them too.
    set((s) => ({
      linkedinCockpitForm: {
        ...s.linkedinCockpitForm,
        strategyId,
        documentIds: [],
        stylePresetId: '',
        useCompetitor: false,
        competitorPostIds: [],
        promptOverride: null,
      },
      linkedinEngineConfig: null,
      linkedinCockpitScores: null,
      linkedinGeneratedContent: null,
      linkedinGeneratedId: null,
      linkedinGeneratedPromptSystem: null,
      linkedinGeneratedPromptUser: null,
      linkedinGeneratedIsTestMode: false,
      linkedinGenerateError: null,
      linkedinPromptPreview: null,
      // Picker state is strategy-scoped too · clear it so switching accounts never
      // flashes the previous account's hooks/angles/branches before a refetch.
      linkedinHookPickerOpen: false,
      linkedinHookOptions: [],
      linkedinHookLoading: false,
      linkedinHookFromCache: false,
      linkedinHookError: null,
      linkedinAnglePickerOpen: false,
      linkedinAngleOptions: [],
      linkedinAngleLoading: false,
      linkedinAngleFromCache: false,
      linkedinAngleError: null,
      linkedinBranchPickerOpen: false,
      linkedinBranchHookOptions: [],
      linkedinBranchLoading: false,
      linkedinBranchFromCache: false,
      linkedinBranchError: null,
      // Presets sind strategie-gebunden: alte Liste sofort weg, sonst zeigt der
      // Wechsel kurz die Presets der vorigen Strategie an.
      linkedinPresets: [],
      linkedinActivePresetId: null,
      linkedinPresetDirty: false,
      linkedinPresetError: null,
    }));
  },

  fetchLinkedInEngineConfig: async (strategyId) => {
    set({ linkedinEngineConfigLoading: true });
    try {
      const cfg = await contentStudioApi.getEngineConfig(strategyId);
      // Guard against stale response (user switched strategy mid-flight)
      if (get().linkedinCockpitForm.strategyId === strategyId) {
        set({ linkedinEngineConfig: cfg, linkedinEngineConfigLoading: false });
      } else {
        set({ linkedinEngineConfigLoading: false });
      }
    } catch {
      set({ linkedinEngineConfigLoading: false });
    }
  },

  fetchLinkedInCockpitScores: async (strategyId) => {
    const f = get().linkedinCockpitForm;
    set({ linkedinCockpitScoresLoading: true });
    try {
      const scores = await contentStudioApi.getCockpitScores(strategyId, {
        content_type: 'linkedin_text_post',
        content_lang: f.language,
        tension_category: f.tensionCategory,
        hook_formula: f.hookFormula,
        experiments: f.experiments,
        topic_angle: f.topicAngle,
        custom_rules: f.customRules,
        preset_id: f.stylePresetId === '' ? null : String(f.stylePresetId),
        model: f.model,
        competitor_count: f.useCompetitor ? f.competitorPostIds.length : 0,
        document_ids: f.documentIds,
      });
      if (get().linkedinCockpitForm.strategyId === strategyId) {
        set({ linkedinCockpitScores: scores, linkedinCockpitScoresLoading: false });
      } else {
        set({ linkedinCockpitScoresLoading: false });
      }
    } catch {
      set({ linkedinCockpitScoresLoading: false });
    }
  },

  previewLinkedInPrompt: async () => {
    const f = get().linkedinCockpitForm;
    if (!f.strategyId) return;
    set({ linkedinPromptPreviewLoading: true });
    try {
      const body = buildRequestBody(f, false);
      const res = await apiPreviewLinkedInPrompt(body);
      set({
        linkedinPromptPreview: { system: res.prompt_system, user: res.prompt_user },
        linkedinPromptPreviewLoading: false,
      });
    } catch {
      set({ linkedinPromptPreviewLoading: false });
    }
  },

  clearLinkedInPromptPreview: () => set({ linkedinPromptPreview: null }),

  generateLinkedInPostWithCockpit: async (opts) => {
    const f = get().linkedinCockpitForm;
    if (!f.strategyId) return;
    const testMode = opts?.testMode ?? false;
    set({
      linkedinGenerating: true,
      linkedinGenerateError: null,
      linkedinGeneratedContent: null,
      linkedinGeneratedId: null,
      linkedinGeneratedPromptSystem: null,
      linkedinGeneratedPromptUser: null,
      linkedinGeneratedIsTestMode: false,
      linkedinGeneratedQuellen: null,
      linkedinGeneratedBrief: null,
      linkedinKandidaten: null,
      linkedinKandidatenPostId: null,
      linkedinVerworfenAnzahl: 0,
      linkedinJudgeUrteil: null,
      linkedinJudgeUebereinstimmung: null,
    });
    try {
      const body = buildRequestBody(f, testMode, opts?.selectedHook);
      const result = await apiGenerateLinkedInPost(body);
      if (testMode) {
        // Test-Modus: einzelner Prompt zur Vorschau, keine Kandidaten.
        set({
          linkedinGenerating: false,
          linkedinGeneratedContent: result.content ?? '',
          linkedinGeneratedId: null,
          linkedinGeneratedPromptSystem: result.prompt_system ?? null,
          linkedinGeneratedPromptUser: result.prompt_user ?? null,
          linkedinGeneratedIsTestMode: true,
          linkedinSelectedStrategyId: f.strategyId,
        });
        return;
      }
      // Stufe D: drei blinde Kandidaten statt eines fertigen Posts -- der Nutzer waehlt
      // (waehleLinkedInKandidat), erst danach steht ein Post in linkedinGeneratedContent.
      set({
        linkedinGenerating: false,
        linkedinKandidaten: result.kandidaten ?? [],
        linkedinKandidatenPostId: result.id ?? null,
        linkedinVerworfenAnzahl: result.verworfen_anzahl ?? 0,
        linkedinGeneratedBrief: result.brief ?? null,
        linkedinSelectedStrategyId: f.strategyId,
      });
    } catch (err) {
      set({
        linkedinGenerating: false,
        linkedinGenerateError: err instanceof Error ? err.message : 'Generation failed. Please try again.',
      });
    }
  },

  waehleLinkedInKandidat: async (kandidatIndex) => {
    const postId = get().linkedinKandidatenPostId;
    const f = get().linkedinCockpitForm;
    if (!postId) return;
    set({ linkedinWahlLoading: true, linkedinGenerateError: null });
    try {
      const result = await apiWaehleLinkedInKandidat(postId, kandidatIndex);
      set({
        linkedinWahlLoading: false,
        linkedinGeneratedContent: result.content,
        linkedinGeneratedId: postId,
        linkedinGeneratedIsTestMode: false,
        linkedinGeneratedQuellen: { erklaerung: result.erklaerung, funde: result.funde ?? [], badge: Boolean(result.badge) },
        linkedinJudgeUrteil: result.judge,
        linkedinJudgeUebereinstimmung: result.judge_uebereinstimmung,
        // Kandidaten-Anzeige raeumen -- die Wahl ist getroffen, jetzt steht der fertige Post.
        linkedinKandidaten: null,
        linkedinKandidatenPostId: null,
      });
      if (f.strategyId) void get().fetchLinkedInPosts(f.strategyId);
    } catch (err) {
      set({
        linkedinWahlLoading: false,
        linkedinGenerateError: err instanceof Error ? err.message : 'Wahl fehlgeschlagen. Bitte erneut versuchen.',
      });
    }
  },

  lehneLinkedInKandidatenAb: async (gruende) => {
    const postId = get().linkedinKandidatenPostId;
    if (!postId) return;
    set({ linkedinAblehnenLoading: true });
    try {
      await apiLehneLinkedInKandidatenAb(postId, gruende);
      set({
        linkedinAblehnenLoading: false,
        linkedinKandidaten: null,
        linkedinKandidatenPostId: null,
        linkedinVerworfenAnzahl: 0,
      });
      // Sofort neu generieren -- derselbe Klick-Fluss wie beim ersten "Generieren".
      await get().generateLinkedInPostWithCockpit({ testMode: false });
    } catch (err) {
      set({
        linkedinAblehnenLoading: false,
        linkedinGenerateError: err instanceof Error ? err.message : 'Ablehnen fehlgeschlagen. Bitte erneut versuchen.',
      });
    }
  },

  clearLinkedInGenerated: () =>
    set({
      linkedinGeneratedContent: null,
      linkedinGeneratedId: null,
      linkedinGeneratedPromptSystem: null,
      linkedinGeneratedPromptUser: null,
      linkedinGeneratedIsTestMode: false,
      linkedinGeneratedQuellen: null,
      linkedinGeneratedBrief: null,
      linkedinKandidaten: null,
      linkedinKandidatenPostId: null,
      linkedinVerworfenAnzahl: 0,
      linkedinJudgeUrteil: null,
      linkedinJudgeUebereinstimmung: null,
      linkedinGenerateError: null,
    }),

  // ---- Hook picker actions ----

  // Opening the dialog is FREE now: it lands on the skeleton library and fetches
  // nothing from the AI. The cache-first suggest moved to ensureLinkedInHooks(),
  // which fires when the user actually switches to the "Vorschlagen lassen" tab.
  openLinkedInHookPicker: async () => {
    const f = get().linkedinCockpitForm;
    if (!f.strategyId) return;
    set({
      linkedinHookPickerOpen: true,
      linkedinHooksLoaded: false,
      linkedinHookOptions: [],
      linkedinHookError: null,
      linkedinLibDraft: null,
      linkedinLibDraftNeedsNumber: false,
      linkedinLibVariants: [],
    });
    await get().loadLinkedInLibrary();
  },

  ensureLinkedInHooks: async () => {
    if (get().linkedinHooksLoaded || get().linkedinHookLoading) return;
    const f = get().linkedinCockpitForm;
    if (!f.strategyId) return;
    set({ linkedinHookLoading: true, linkedinHookError: null });
    try {
      const res = await apiSuggestLinkedInHooks({
        strategy_id: f.strategyId,
        topic_angle: f.topicAngle.trim() || undefined,
        language: f.language,
        document_ids: f.documentIds.length > 0 ? f.documentIds : undefined,
        tension_category: f.tensionCategory,
        force_new: false, // cache-first: no tokens if hooks already saved
      });
      set({
        linkedinHookOptions: res.options,
        linkedinHookFromCache: res.from_cache,
        linkedinHookLoading: false,
        linkedinHooksLoaded: true,
      });
    } catch (err) {
      set({
        linkedinHookLoading: false,
        linkedinHookError: err instanceof Error ? err.message : 'Failed to load hooks.',
      });
    }
  },

  rerollLinkedInHooks: async () => {
    const f = get().linkedinCockpitForm;
    if (!f.strategyId) return;
    set({ linkedinHookLoading: true, linkedinHookError: null });
    try {
      const res = await apiSuggestLinkedInHooks({
        strategy_id: f.strategyId,
        topic_angle: f.topicAngle.trim() || undefined,
        language: f.language,
        document_ids: f.documentIds.length > 0 ? f.documentIds : undefined,
        tension_category: f.tensionCategory,
        force_new: true, // spends tokens for fresh hooks
      });
      set({
        linkedinHookOptions: res.options,
        linkedinHookFromCache: res.from_cache,
        linkedinHookLoading: false,
        linkedinHooksLoaded: true,
      });
    } catch (err) {
      set({
        linkedinHookLoading: false,
        linkedinHookError: err instanceof Error ? err.message : 'Failed to load hooks.',
      });
    }
  },

  // ---- Hook library actions (free unless stated) ----

  loadLinkedInLibrary: async (opts) => {
    const f = get().linkedinCockpitForm;
    if (!f.strategyId) return;
    const append = !!opts?.append;
    set(append ? { linkedinLibLoadingMore: true } : { linkedinLibLoading: true, linkedinLibError: null });
    try {
      const res = await apiBrowseHookLibrary({
        strategy_id: f.strategyId,
        category: get().linkedinLibCategory,
        q: get().linkedinLibQuery.trim() || undefined,
        offset: append ? get().linkedinLibItems.length : 0,
        limit: 30,
      });
      set((s) => ({
        linkedinLibItems: append ? [...s.linkedinLibItems, ...res.items] : res.items,
        linkedinLibCounts: res.counts,
        linkedinLibTotal: res.total,
        linkedinLibLoading: false,
        linkedinLibLoadingMore: false,
      }));
    } catch (err) {
      set({
        linkedinLibLoading: false,
        linkedinLibLoadingMore: false,
        linkedinLibError: err instanceof Error ? err.message : 'Bibliothek nicht erreichbar.',
      });
    }
  },

  setLinkedInLibraryCategory: async (c) => {
    set({ linkedinLibCategory: c, linkedinLibItems: [] });
    await get().loadLinkedInLibrary();
  },

  // Typing is local-only; the caller debounces the reload so every keystroke
  // does not become a request.
  setLinkedInLibraryQuery: (q) => set({ linkedinLibQuery: q }),

  // The ONE paid step of the library flow: fill a single hand-picked skeleton.
  fillLinkedInSkeleton: async (hook) => {
    const f = get().linkedinCockpitForm;
    if (!f.strategyId) return;
    set({ linkedinLibFillingId: hook.id, linkedinLibError: null });
    try {
      const res = await apiFillHookFromLibrary({
        strategy_id: f.strategyId,
        hook_id: hook.id,
        topic_angle: f.topicAngle.trim() || undefined,
        language: f.language,
        document_ids: f.documentIds.length > 0 ? f.documentIds : undefined,
        tension_category: f.tensionCategory,
      });
      const draft = res.options[0] ?? null;
      // Load every stored fill of this skeleton so earlier versions stay reachable.
      let variants: LinkedInHookOption[] = res.options;
      try {
        variants = (await apiListHookVariants(f.strategyId, hook.id)).variants;
      } catch {
        /* the draft alone is enough to keep working */
      }
      set({
        linkedinLibFillingId: null,
        linkedinLibDraft: draft,
        linkedinLibDraftNeedsNumber: res.needs_number,
        linkedinLibVariants: variants,
      });
    } catch (err) {
      set({
        linkedinLibFillingId: null,
        linkedinLibError: err instanceof Error ? err.message : 'Ausfüllen fehlgeschlagen.',
      });
    }
  },

  saveLinkedInVariantEdit: async (cacheId, text) => {
    try {
      const row = await apiEditHookVariant(cacheId, text);
      set((s) => ({
        linkedinLibDraft: row,
        linkedinLibVariants: s.linkedinLibVariants.map((v) => (v.cache_id === cacheId ? row : v)),
      }));
    } catch (err) {
      set({ linkedinLibError: err instanceof Error ? err.message : 'Speichern fehlgeschlagen.' });
    }
  },

  deleteLinkedInVariant: async (cacheId) => {
    try {
      await apiDeleteHookVariant(cacheId);
      set((s) => ({
        linkedinLibVariants: s.linkedinLibVariants.filter((v) => v.cache_id !== cacheId),
      }));
    } catch (err) {
      set({ linkedinLibError: err instanceof Error ? err.message : 'Löschen fehlgeschlagen.' });
    }
  },

  useLinkedInVariant: (opt) => set({ linkedinLibDraft: opt }),

  discardLinkedInDraft: () =>
    set({ linkedinLibDraft: null, linkedinLibDraftNeedsNumber: false, linkedinLibVariants: [] }),

  pickLinkedInHook: async (opt) => {
    // Close the picker, mark the chosen hook as tested locally (badge updates on reopen),
    // then generate the post anchored on it.
    set((s) => ({
      linkedinHookPickerOpen: false,
      linkedinHookOptions: s.linkedinHookOptions.map((o) =>
        o.cache_id && o.cache_id === opt.cache_id ? { ...o, used: true } : o,
      ),
    }));
    await get().generateLinkedInPostWithCockpit({ selectedHook: opt });
  },

  closeLinkedInHookPicker: () => set({ linkedinHookPickerOpen: false }),

  // ---- Angle picker actions ----

  openLinkedInAnglePicker: async () => {
    const f = get().linkedinCockpitForm;
    if (!f.strategyId) return;
    if (f.documentIds.length === 0) {
      set({ linkedinAnglePickerOpen: true, linkedinAngleOptions: [], linkedinAngleLoading: false,
        linkedinAngleError: 'Select a document first · angles are derived from it.' });
      return;
    }
    set({ linkedinAnglePickerOpen: true, linkedinAngleLoading: true, linkedinAngleError: null });
    try {
      const res = await apiSuggestLinkedInAngles({
        strategy_id: f.strategyId,
        language: f.language,
        document_ids: f.documentIds,
        force_new: false, // cache-first: no tokens if angles already saved for this doc set
      });
      set({ linkedinAngleOptions: res.options, linkedinAngleFromCache: res.from_cache, linkedinAngleLoading: false });
    } catch (err) {
      set({ linkedinAngleLoading: false,
        linkedinAngleError: err instanceof Error ? err.message : 'Failed to load angles.' });
    }
  },

  rerollLinkedInAngles: async () => {
    const f = get().linkedinCockpitForm;
    if (!f.strategyId || f.documentIds.length === 0) return;
    set({ linkedinAngleLoading: true, linkedinAngleError: null });
    try {
      const res = await apiSuggestLinkedInAngles({
        strategy_id: f.strategyId,
        language: f.language,
        document_ids: f.documentIds,
        force_new: true, // spends tokens for fresh angles
      });
      set({ linkedinAngleOptions: res.options, linkedinAngleFromCache: res.from_cache, linkedinAngleLoading: false });
    } catch (err) {
      set({ linkedinAngleLoading: false,
        linkedinAngleError: err instanceof Error ? err.message : 'Failed to load angles.' });
    }
  },

  pickLinkedInAngle: (opt) => {
    // An angle just fills the Topic field; the user then generates hooks/post as usual.
    set((s) => ({
      linkedinAnglePickerOpen: false,
      linkedinCockpitForm: { ...s.linkedinCockpitForm, topicAngle: opt.angle_text },
      linkedinAngleOptions: s.linkedinAngleOptions.map((o) =>
        o.cache_id && o.cache_id === opt.cache_id ? { ...o, used: true } : o,
      ),
    }));
  },

  // --- Vault -> Content Engine -------------------------------------------------
  //
  // Zwei Stufen, weil die Vokabulare disjunkt sind. Stufe A schreibt nur, was der
  // Router auf jeden Fall annimmt (Freitext + null). Stufe B setzt die Hook-Formel
  // erst, wenn die engineConfig da ist und die id dort wirklich vorkommt — sonst
  // stuende ein roher Slug im Chip und `POST /linkedin/generate` antwortete 422.
  startLinkedInVaultHandover: ({ topicAngle, hookFormula, strategyId, vaultSourceMeta }) => {
    const s = get();
    // Die Strategie ZUERST: ihr Reset leert Dokumente/Presets/Picker und nullt
    // engineConfig — danach wartet Stufe B auf die Config der Strategie, mit der
    // wirklich generiert wird. topicAngle/tensionCategory/hookFormula ruehrt der
    // Reset ausdruecklich nicht an, die Reihenfolge ist deshalb sicher.
    // `strategyId` muss der Aufrufer bereits gegen `strategies` geprueft haben —
    // diese Slice sieht die Strategie-Liste nicht (StateCreator<LinkedInSlice>).
    if (strategyId != null && strategyId !== s.linkedinCockpitForm.strategyId) {
      s.setLinkedInCockpitStrategy(strategyId);
    }
    // vaultSourcePostId/-Mode/-Meta brauchen KEINE Stufe B: post_id ist ein Int,
    // mode hat einen festen Default — anders als hookFormula gibt es hier keinen
    // 422-Fall, der auf die engineConfig warten muesste. Direkt setzen, und den
    // Hinweis-Banner gleich mit — die Create-Tab-Oberflaeche klappt daraufhin die
    // Vorlage-Karte auf.
    set((st) => ({
      linkedinCockpitForm: {
        ...st.linkedinCockpitForm,
        topicAngle,
        tensionCategory: null,
        hookFormula: null,
        vaultSourcePostId: vaultSourceMeta?.postId ?? null,
        vaultSourceMode: 'skeleton',
        vaultSourceMeta,
      },
      linkedinPromptPreview: null,
      linkedinPresetDirty: true,
      linkedinPendingVaultHandover: { hookFormula },
      linkedinVaultHandoverJustApplied: vaultSourceMeta,
      linkedinActiveTab: 'create',
    }));
  },

  clearLinkedInVaultHandoverBanner: () => set({ linkedinVaultHandoverJustApplied: null }),

  applyLinkedInVaultHandover: () => {
    const s = get();
    const pending = s.linkedinPendingVaultHandover;
    const cfg = s.linkedinEngineConfig;
    if (!pending || !cfg) return;
    const wanted = pending.hookFormula;
    // Der zweite Riegel neben der Tabelle: die Engine ist die Autoritaet, nicht wir.
    const valid = wanted != null && cfg.hook_formulas.some((h) => h.id === wanted);
    set((st) => ({
      linkedinPendingVaultHandover: null,
      // Wer in der Zwischenzeit selbst eine Formel gewaehlt hat, behaelt sie.
      linkedinCockpitForm:
        valid && st.linkedinCockpitForm.hookFormula === null
          ? { ...st.linkedinCockpitForm, hookFormula: wanted }
          : st.linkedinCockpitForm,
    }));
  },

  closeLinkedInAnglePicker: () => set({ linkedinAnglePickerOpen: false }),

  // ---- Branch (hook A/B) actions ----

  openLinkedInBranchPicker: async (post) => {
    // The post's opening line(s) seed the hook topic so candidates stay on-theme.
    const hint = (post.content || '').replace(/\s+/g, ' ').trim().slice(0, 180);
    set({
      linkedinBranchPickerOpen: true,
      linkedinBranchTargetId: post.id,
      linkedinBranchTargetStrategyId: post.strategy_id,
      linkedinBranchTopicHint: hint,
      linkedinBranchLoading: true,
      linkedinBranchError: null,
      linkedinBranchHookOptions: [],
    });
    try {
      // No `language` on purpose: this picker runs against post.strategy_id, which
      // may be a DIFFERENT strategy than the one loaded in the cockpit — sending the
      // cockpit language would then be wrong. Omitting it makes the server resolve
      // the language of THAT strategy (account_language). Before the fix the server
      // hard-defaulted to "en", so branch hooks were always English.
      const res = await apiSuggestLinkedInHooks({
        strategy_id: post.strategy_id,
        topic_angle: hint || undefined,
        force_new: false,
      });
      set({ linkedinBranchHookOptions: res.options, linkedinBranchFromCache: res.from_cache, linkedinBranchLoading: false });
    } catch (err) {
      set({ linkedinBranchLoading: false,
        linkedinBranchError: err instanceof Error ? err.message : 'Failed to load hooks.' });
    }
  },

  rerollLinkedInBranchHooks: async () => {
    const sid = get().linkedinBranchTargetStrategyId;
    const hint = get().linkedinBranchTopicHint;
    if (!sid) return;
    set({ linkedinBranchLoading: true, linkedinBranchError: null });
    try {
      // Same reasoning as openLinkedInBranchPicker: the server resolves the
      // language from this strategy's own setting, not from the cockpit.
      const res = await apiSuggestLinkedInHooks({
        strategy_id: sid,
        topic_angle: hint || undefined,
        force_new: true,
      });
      set({ linkedinBranchHookOptions: res.options, linkedinBranchFromCache: res.from_cache, linkedinBranchLoading: false });
    } catch (err) {
      set({ linkedinBranchLoading: false,
        linkedinBranchError: err instanceof Error ? err.message : 'Failed to load hooks.' });
    }
  },

  pickLinkedInBranchHook: async (opt) => {
    const targetId = get().linkedinBranchTargetId;
    if (!targetId) return;
    set({ linkedinBranching: true, linkedinBranchError: null });
    try {
      await apiBranchLinkedInPost({
        generation_id: targetId,
        selected_hook: {
          hook_id: opt.hook_id ?? null,
          filled_text: opt.filled_text,
          template: opt.template ?? null,
          category: opt.category ?? null,
        },
        hook_cache_id: opt.cache_id,
      });
      set({ linkedinBranching: false, linkedinBranchPickerOpen: false, linkedinBranchTargetId: null });
      const sid = get().linkedinBranchTargetStrategyId ?? undefined;
      void get().fetchLinkedInPosts(sid);
    } catch (err) {
      set({ linkedinBranching: false,
        linkedinBranchError: err instanceof Error ? err.message : 'Variante fehlgeschlagen.' });
    }
  },

  closeLinkedInBranchPicker: () =>
    set({ linkedinBranchPickerOpen: false, linkedinBranchTargetId: null, linkedinBranching: false }),

  rewriteLinkedInPost: async (id, targetExcerpt) => {
    set({ linkedinRewritingId: id });
    try {
      const res = await apiRewriteLinkedInPost({
        generation_id: id,
        language: get().linkedinCockpitForm.language,
        target_excerpt: targetExcerpt,
      });
      return res.content || null;
    } catch {
      return null;
    } finally {
      set({ linkedinRewritingId: null });
    }
  },

  // ---- Launch sequence actions ----

  suggestLaunch: async (req) => {
    set({ linkedinLaunchSuggesting: true, linkedinLaunchError: null, linkedinLaunchResults: null });
    try {
      const plan = await apiSuggestLaunchSequence(req);
      set({ linkedinLaunchPlan: plan, linkedinLaunchSuggesting: false });
    } catch (err) {
      set({
        linkedinLaunchSuggesting: false,
        linkedinLaunchError: err instanceof Error ? err.message : 'Launch-Vorschlag fehlgeschlagen.',
      });
    }
  },

  generateLaunch: async (req) => {
    set({ linkedinLaunchGenerating: true, linkedinLaunchError: null });
    try {
      const res = await apiGenerateLaunchSequence(req);
      set((s) => ({
        linkedinLaunchGenerating: false,
        // Merge retry-missing results into prior ones (keep already-generated roles).
        linkedinLaunchResults: req.only_roles && s.linkedinLaunchResults
          ? {
              ...res,
              results: [
                ...s.linkedinLaunchResults.results.filter(
                  (p) => !res.results.some((n) => n.role === p.role),
                ),
                ...res.results,
              ].sort((a, b) => a.order - b.order),
            }
          : res,
      }));
      // Refresh the queue so the new drafts show up.
      if (!res.warning && req.strategy_id) void get().fetchLinkedInPosts(req.strategy_id);
    } catch (err) {
      set({
        linkedinLaunchGenerating: false,
        linkedinLaunchError: err instanceof Error ? err.message : 'Launch-Generierung fehlgeschlagen.',
      });
    }
  },

  clearLaunch: () =>
    set({ linkedinLaunchPlan: null, linkedinLaunchResults: null, linkedinLaunchError: null }),

  // --- Cockpit-Presets ------------------------------------------------------
  linkedinPresets: [],
  linkedinPresetsLoading: false,
  linkedinActivePresetId: null,
  linkedinPresetDirty: false,
  linkedinPresetError: null,

  fetchLinkedInPresets: async (strategyId) => {
    set({ linkedinPresetsLoading: true, linkedinPresetError: null });
    try {
      const res = await apiFetchLinkedInPresets(strategyId);
      set({ linkedinPresets: res.presets, linkedinPresetsLoading: false });
    } catch (err) {
      set({
        linkedinPresetsLoading: false,
        linkedinPresetError: err instanceof Error ? err.message : 'Presets laden fehlgeschlagen.',
      });
    }
  },

  saveLinkedInPreset: async (name) => {
    const form = get().linkedinCockpitForm;
    if (!form.strategyId) return;
    set({ linkedinPresetError: null });
    try {
      const preset = await apiCreateLinkedInPreset(form.strategyId, name, extractPresetConfig(form));
      set((s) => ({
        // Gleicher Name ueberschreibt serverseitig — hier dieselbe Regel, damit die
        // Liste nach dem Speichern nicht denselben Namen zweimal zeigt.
        linkedinPresets: [...s.linkedinPresets.filter((p) => p.id !== preset.id), preset].sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
        linkedinActivePresetId: preset.id,
        linkedinPresetDirty: false,
      }));
    } catch (err) {
      set({ linkedinPresetError: err instanceof Error ? err.message : 'Speichern fehlgeschlagen.' });
    }
  },

  updateActiveLinkedInPreset: async () => {
    const { linkedinActivePresetId: id, linkedinCockpitForm: form } = get();
    if (!id || !form.strategyId) return;
    set({ linkedinPresetError: null });
    try {
      const preset = await apiUpdateLinkedInPreset(form.strategyId, id, {
        config: extractPresetConfig(form),
      });
      set((s) => ({
        linkedinPresets: s.linkedinPresets.map((p) => (p.id === id ? preset : p)),
        linkedinPresetDirty: false,
      }));
    } catch (err) {
      set({ linkedinPresetError: err instanceof Error ? err.message : 'Speichern fehlgeschlagen.' });
    }
  },

  applyLinkedInPreset: (presetId) => {
    const preset = get().linkedinPresets.find((p) => p.id === presetId);
    if (!preset) return;
    set((s) => ({
      linkedinCockpitForm: {
        ...s.linkedinCockpitForm,
        ...presetConfigToPatch(preset.config),
      },
      linkedinActivePresetId: presetId,
      linkedinPresetDirty: false,
      linkedinPromptPreview: null,
      linkedinPresetError: null,
    }));
  },

  removeLinkedInPreset: async (presetId) => {
    const strategyId = get().linkedinCockpitForm.strategyId;
    if (!strategyId) return;
    try {
      await apiDeleteLinkedInPreset(strategyId, presetId);
      set((s) => ({
        linkedinPresets: s.linkedinPresets.filter((p) => p.id !== presetId),
        linkedinActivePresetId: s.linkedinActivePresetId === presetId ? null : s.linkedinActivePresetId,
      }));
    } catch (err) {
      set({ linkedinPresetError: err instanceof Error ? err.message : 'Löschen fehlgeschlagen.' });
    }
  },

  clearActiveLinkedInPreset: () => set({ linkedinActivePresetId: null, linkedinPresetDirty: false }),

  // --- Prompt-Bausteine -----------------------------------------------------
  linkedinPromptFragments: [],
  linkedinPromptFragmentsLoading: false,
  linkedinPromptFragmentsLoaded: false,
  linkedinPromptFragmentsCanEdit: false,

  fetchLinkedInPromptFragments: async ({ force } = {}) => {
    // Die Bausteine sind global und aendern sich selten — einmal pro Sitzung
    // reicht. `force` nach einem Schreibvorgang.
    if (get().linkedinPromptFragmentsLoaded && !force) return;
    set({ linkedinPromptFragmentsLoading: true });
    try {
      const res = await apiFetchLinkedInPromptFragments();
      set({
        linkedinPromptFragments: res.fragments,
        linkedinPromptFragmentsCanEdit: res.can_edit,
        linkedinPromptFragmentsLoaded: true,
        linkedinPromptFragmentsLoading: false,
      });
    } catch {
      set({ linkedinPromptFragmentsLoading: false });
    }
  },

  saveLinkedInPromptFragment: async (key, text) => {
    await apiSaveLinkedInPromptFragment(key, text);
    set((s) => ({
      linkedinPromptFragments: s.linkedinPromptFragments.map((f) =>
        f.key === key ? { ...f, override_text: text, is_overridden: true, chars: text.length } : f,
      ),
      // Vorschau ist jetzt veraltet: sie wurde mit dem alten Baustein gebaut.
      linkedinPromptPreview: null,
    }));
  },

  resetLinkedInPromptFragment: async (key) => {
    await apiResetLinkedInPromptFragment(key);
    set((s) => ({
      linkedinPromptFragments: s.linkedinPromptFragments.map((f) =>
        f.key === key
          ? { ...f, override_text: null, is_overridden: false, chars: f.default_text.length }
          : f,
      ),
      linkedinPromptPreview: null,
    }));
  },
});
